import { Form, Formik } from 'formik';
import { type FC } from 'react';

import { OrderType } from '@/api/order/models/OrderType';
import { useCurrentUserInfo } from '@/auth/hooks';
import { Show } from '@/components/Show';
import { useTexts } from '@/hooks/useTexts';
import { useOrderSteps } from '@/pages/Order/components/OrderStepsProvider';
import { useOrderPageContext } from '@/pages/Order/OrderPageContext';
import { FundOrder } from '@/pages/Order/types/Order';

import { useFundPortfolios } from '../../hooks/useFundPortfolios';
import { SubscriptionOrderFormValues } from '../../types/SubscriptionOrderFormValues';
import { mapSubscriptionFundStatesToOrderPlacements } from '../../utils/mapSubscriptionFundStatesToOrderPlacements';
import { useCreateSubscriptionValidationSchema } from '../../validationSchemas/hooks/useCreateSubscriptionValidationSchema';
import { useInitialSubscriptionFormValues } from './hooks/useInitialSubscriptionFormValues';
import { SubscriptionFormContent } from './SubscriptionFormContent';

export const SubscriptionOrderForm: FC = () => {
  const { orders } = useTexts();
  const userInfo = useCurrentUserInfo();

  const { completeCurrentStep } = useOrderSteps();
  const { setCurrentOrder } = useOrderPageContext<FundOrder>();

  const portfolios = useFundPortfolios(OrderType.Subscription);
  const hasNoFundPortfolio = portfolios.length === 0;

  const initialValues = useInitialSubscriptionFormValues();
  const validationSchema = useCreateSubscriptionValidationSchema();

  const handleSubmit = (values: SubscriptionOrderFormValues): void => {
    const orderPlacements = mapSubscriptionFundStatesToOrderPlacements(
      values.toFunds,
      values.payments,
    );

    const fundOrder: FundOrder = {
      orderType: OrderType.Subscription,
      userId: userInfo.userCmId || userInfo.signature!,
      portfolioId: values.toPortfolioShortName,
      signatories: [values.signer, values.cosigner].filter(
        (value) => value !== undefined && value !== null,
      ),
      payments: values.payments,
      orderPlacements,
      signingProvider: values.signingProvider,
    };

    setCurrentOrder(fundOrder);
    completeCurrentStep();
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      <Form data-test="subscription-order-form">
        <Show when={hasNoFundPortfolio}>
          <h3 data-test="no-fund-portfolio">
            {orders.form.subscription.noFundPortfolios}
          </h3>
        </Show>
        <SubscriptionFormContent />
      </Form>
    </Formik>
  );
};
